/**
 * Public Online Booking Page (Calendly-style)
 * 
 * Accessible at /book/:state/:city/:slug
 * URL params: ?month=2026-01&date=2026-01-23&vet=abc123
 * No authentication required.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Clock, MapPin, Phone, ArrowLeft, Check, Loader2 } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import { APP_NAME } from '../config/branding';
import CalendlyCalendar from '../components/booking/CalendlyCalendar';
import TimeSlotPanel from '../components/booking/TimeSlotPanel';
import BookingForm from '../components/booking/BookingForm';

interface PracticeInfo {
  id: string;
  name: string;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  timezone: string;
  online_booking_enabled: boolean;
  online_booking_advance_days: number;
  online_booking_min_notice_hours: number;
  hours: Array<{ day: string; open: string; close: string; closed: boolean }> | null;
  accepts_new_patients: boolean;
  photo_url: string | null;
}

interface AvailableSlot {
  date: string;
  time: string;
  start_time: string;
  end_time: string;
  clinician_id: string;
  clinician_name: string | null;
}

interface Clinician {
  id: string;
  name: string;
  title: string;
  photo_url: string | null;
}

type BookingStep = 'calendar' | 'form' | 'confirmation';

// Format phone number: 4157549214 -> (415) 754-9214
const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

// Get current month in YYYY-MM format
const getCurrentMonth = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// Get today's date in YYYY-MM-DD format
const getTodayDate = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const PublicBookingPage: React.FC = () => {
  const { state, city, slug } = useParams<{ state: string; city: string; slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [practice, setPractice] = useState<PracticeInfo | null>(null);
  const [slots, setSlots] = useState<AvailableSlot[]>([]);
  const [clinicians, setClinicians] = useState<Clinician[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);
  const [step, setStep] = useState<BookingStep>('calendar');
  const [submitting, setSubmitting] = useState(false);
  const [confirmationData, setConfirmationData] = useState<{
    confirmation_number: string;
    appointment_datetime: string;
    client_name: string;
    client_email: string;
    pet_name: string;
    visit_reason: string;
    clinician_name: string | null;
    clinician_title: string | null;
    clinician_photo_url: string | null;
  } | null>(null);
  
  // Calendly-style state
  const [currentMonth, setCurrentMonth] = useState(getCurrentMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedClinicianId, setSelectedClinicianId] = useState<string | null>(null);
  const [slotsLoading, setSlotsLoading] = useState(false);

  // Initialize state from URL params on mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const monthParam = searchParams.get('month');
    const dateParam = searchParams.get('date');
    const vetParam = searchParams.get('vet');
    const stepParam = searchParams.get('step');
    
    if (monthParam && /^\d{4}-\d{2}$/.test(monthParam)) {
      setCurrentMonth(monthParam);
    }
    if (dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam)) {
      setSelectedDate(dateParam);
    }
    if (vetParam) {
      setSelectedClinicianId(vetParam);
    }
    if (stepParam === 'form' || stepParam === 'confirmation') {
      setStep(stepParam);
    }
  }, []);

  // Update URL with current state
  const updateUrl = useCallback((params: { 
    month?: string; 
    date?: string | null; 
    vet?: string | null; 
    step?: BookingStep 
  }, replace = false) => {
    const searchParams = new URLSearchParams(location.search);
    
    if (params.month !== undefined) {
      if (params.month === getCurrentMonth()) {
        searchParams.delete('month');
      } else {
        searchParams.set('month', params.month);
      }
    }
    
    if (params.date !== undefined) {
      if (params.date === null) {
        searchParams.delete('date');
      } else {
        searchParams.set('date', params.date);
      }
    }
    
    if (params.vet !== undefined) {
      if (params.vet === null || params.vet === '') {
        searchParams.delete('vet');
      } else {
        searchParams.set('vet', params.vet);
      }
    }
    
    if (params.step !== undefined) {
      if (params.step === 'calendar') {
        searchParams.delete('step');
      } else {
        searchParams.set('step', params.step);
      }
    }
    
    const newSearch = searchParams.toString();
    const newPath = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    
    if (replace) {
      navigate(newPath, { replace: true });
    } else {
      navigate(newPath);
    }
  }, [navigate, location.pathname, location.search]);

  // Navigate to a step with browser history support
  const goToStep = useCallback((newStep: BookingStep, replace = false) => {
    setStep(newStep);
    updateUrl({ step: newStep }, replace);
  }, [updateUrl]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const stepParam = searchParams.get('step') as BookingStep | null;
    const monthParam = searchParams.get('month');
    const dateParam = searchParams.get('date');
    const vetParam = searchParams.get('vet');
    
    // Update month from URL
    if (monthParam && /^\d{4}-\d{2}$/.test(monthParam)) {
      setCurrentMonth(monthParam);
    }
    
    // Update date from URL
    if (dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam)) {
      setSelectedDate(dateParam);
    } else if (!dateParam) {
      setSelectedDate(null);
    }
    
    // Update clinician from URL
    if (vetParam !== selectedClinicianId) {
      setSelectedClinicianId(vetParam);
    }
    
    // Update step from URL
    if (stepParam === 'form' && selectedSlot) {
      setStep('form');
    } else if (stepParam === 'confirmation' && confirmationData) {
      setStep('confirmation');
    } else {
      setStep('calendar');
      if (stepParam === 'form') {
        setSelectedSlot(null);
      }
    }
  }, [location.search]);

  // Load practice info
  useEffect(() => {
    const loadPractice = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/book/${state}/${city}/${slug}`);
        if (!response.ok) {
          if (response.status === 404) {
            setError('Practice not found');
          } else {
            setError('Failed to load practice information');
          }
          return;
        }
        const data = await response.json();
        setPractice(data);
        
        if (!data.online_booking_enabled) {
          setError('Online booking is not available for this practice');
          return;
        }
        
        // Load clinicians
        const clinResponse = await fetch(`${API_BASE_URL}/api/v1/book/${state}/${city}/${slug}/clinicians`);
        if (clinResponse.ok) {
          setClinicians(await clinResponse.json());
        }
      } catch (err) {
        setError('Failed to connect to booking service');
      } finally {
        setLoading(false);
      }
    };
    
    loadPractice();
  }, [state, city, slug]);

  // Load availability for the current month
  const loadAvailability = useCallback(async (month: string) => {
    if (!practice) return;
    
    setSlotsLoading(true);
    
    // Parse month
    const [year, monthNum] = month.split('-').map(Number);
    const startDate = new Date(year, monthNum - 1, 1);
    const endDate = new Date(year, monthNum, 0); // Last day of month
    
    const formatDate = (d: Date) => {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    
    const startStr = formatDate(startDate);
    const endStr = formatDate(endDate);
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/book/${state}/${city}/${slug}/availability?start_date=${startStr}&end_date=${endStr}`
      );
      if (response.ok) {
        const data = await response.json();
        setSlots(data.slots || []);
      }
    } catch (err) {
      console.error('Failed to load availability:', err);
    } finally {
      setSlotsLoading(false);
    }
  }, [practice, state, city, slug]);

  // Load availability when month changes
  useEffect(() => {
    if (practice) {
      loadAvailability(currentMonth);
    }
  }, [currentMonth, practice, loadAvailability]);

  // Compute available dates from slots
  const availableDates = useMemo(() => {
    const dates = new Set<string>();
    let filteredSlots = selectedClinicianId
      ? slots.filter(s => s.clinician_id === selectedClinicianId)
      : slots;
    
    filteredSlots.forEach(slot => dates.add(slot.date));
    return dates;
  }, [slots, selectedClinicianId]);

  // Get slots for the selected date
  const slotsForSelectedDate = useMemo(() => {
    if (!selectedDate) return [];
    
    let filteredSlots = slots.filter(s => s.date === selectedDate);
    
    if (selectedClinicianId) {
      filteredSlots = filteredSlots.filter(s => s.clinician_id === selectedClinicianId);
    } else {
      // Deduplicate by time when no specific vet is selected
      const seenTimes = new Set<string>();
      filteredSlots = filteredSlots.filter(slot => {
        if (seenTimes.has(slot.time)) return false;
        seenTimes.add(slot.time);
        return true;
      });
    }
    
    return filteredSlots.sort((a, b) => a.time.localeCompare(b.time));
  }, [slots, selectedDate, selectedClinicianId]);

  // Calculate min/max dates for booking window
  const { minDate, maxDate } = useMemo(() => {
    const today = new Date();
    const min = getTodayDate();
    
    const maxDateObj = new Date(today);
    maxDateObj.setDate(today.getDate() + (practice?.online_booking_advance_days || 14));
    const max = `${maxDateObj.getFullYear()}-${String(maxDateObj.getMonth() + 1).padStart(2, '0')}-${String(maxDateObj.getDate()).padStart(2, '0')}`;
    
    return { minDate: min, maxDate: max };
  }, [practice?.online_booking_advance_days]);

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    updateUrl({ date });
  };

  const handleMonthChange = (month: string) => {
    setCurrentMonth(month);
    setSelectedDate(null); // Clear selected date when changing months
    updateUrl({ month, date: null });
  };

  const handleSlotSelect = (slot: AvailableSlot) => {
    setSelectedSlot(slot);
    goToStep('form');
  };

  const handleClinicianChange = (vetId: string | null) => {
    setSelectedClinicianId(vetId);
    updateUrl({ vet: vetId });
  };

  const handleFormSubmit = async (formData: {
    phone: string;
    email: string;
    name: string;
    petName: string;
    petSpecies: string;
    petBreed: string;
    visitReason: string;
    notes: string;
  }) => {
    if (!selectedSlot || !practice) return;
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/book/${state}/${city}/${slug}/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_phone: formData.phone,
          customer_email: formData.email,
          customer_name: formData.name,
          pet_name: formData.petName,
          pet_species: formData.petSpecies,
          pet_breed: formData.petBreed || null,
          requested_date: selectedSlot.date,
          requested_time: selectedSlot.time,
          clinician_id: selectedSlot.clinician_id,
          visit_reason: formData.visitReason,
          notes: formData.notes || null,
          consent_given: true
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const clinician = clinicians.find(c => c.id === selectedSlot.clinician_id);
        setConfirmationData({
          confirmation_number: data.confirmation_number,
          appointment_datetime: data.appointment_datetime,
          client_name: formData.name,
          client_email: formData.email,
          pet_name: formData.petName,
          visit_reason: formData.visitReason,
          clinician_name: clinician?.name || selectedSlot.clinician_name || null,
          clinician_title: 'Veterinarian',
          clinician_photo_url: clinician?.photo_url || null
        });
        goToStep('confirmation', true);
      } else {
        const errorData = await response.json();
        alert(errorData.detail?.message || errorData.detail || 'Failed to submit booking');
      }
    } catch (err) {
      alert('Failed to submit booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading booking information...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !practice) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-gray-400" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            {error || 'Practice not found'}
          </h1>
          <p className="text-gray-600 mb-6">
            Please check the URL or contact the practice directly.
          </p>
          <Link
            to="/"
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Return to {APP_NAME}
          </Link>
        </div>
      </div>
    );
  }

  // Form step
  if (step === 'form' && selectedSlot) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <button
            onClick={() => goToStep('calendar')}
            className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to calendar
          </button>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            {/* Selected time summary */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-gray-900">
                    {new Date(selectedSlot.date + 'T12:00:00').toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                  <p className="text-sm text-gray-600">
                    {(() => {
                      const [hours, minutes] = selectedSlot.time.split(':');
                      const hour = parseInt(hours, 10);
                      const ampm = hour >= 12 ? 'pm' : 'am';
                      const hour12 = hour % 12 || 12;
                      return `${hour12}:${minutes}${ampm}`;
                    })()}
                    {selectedSlot.clinician_name && ` with ${selectedSlot.clinician_name}`}
                  </p>
                </div>
              </div>
            </div>
            
            <BookingForm
              onSubmit={handleFormSubmit}
              submitting={submitting}
              practiceName={practice.name}
            />
          </div>
        </div>
      </div>
    );
  }

  // Confirmation step
  if (step === 'confirmation' && confirmationData) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="max-w-xl mx-auto px-4 py-8">
          {/* Client & Request Info */}
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">{confirmationData.client_name}</h1>
            <p className="text-gray-500">
              {confirmationData.visit_reason} for {confirmationData.pet_name}
            </p>
          </div>

          {/* Status Banner */}
          <div className="bg-green-700 rounded-lg p-5 mb-6">
            <p className="text-green-200 text-sm font-medium mb-1">Status</p>
            <h2 className="text-white text-xl font-semibold mb-1">
              Appointment Confirmed!
            </h2>
            <p className="text-green-100 text-sm">
              Check your inbox ({confirmationData.client_email}) for a calendar invite with more details.
            </p>
          </div>

          {/* Confirmation Details */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
            {/* When */}
            <div className="border-b border-gray-200 pb-5">
              <p className="text-gray-500 text-sm mb-1">When</p>
              <p className="text-gray-900 font-semibold">
                {new Date(confirmationData.appointment_datetime).toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
              <p className="text-gray-500">
                {new Date(confirmationData.appointment_datetime).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                })}
              </p>
            </div>

            {/* Location */}
            <div className="border-b border-gray-200 pb-5">
              <p className="text-gray-500 text-sm mb-1">Location</p>
              <p className="text-gray-900 font-semibold">{practice.name}</p>
              {practice.address && (
                <p className="text-gray-500">{practice.address}</p>
              )}
            </div>

            {/* Veterinarian */}
            {confirmationData.clinician_name && (
              <div className="border-b border-gray-200 pb-5">
                <p className="text-gray-500 text-sm mb-3">Your Veterinarian</p>
                <div className="flex items-center gap-3">
                  {confirmationData.clinician_photo_url ? (
                    <img 
                      src={confirmationData.clinician_photo_url} 
                      alt={confirmationData.clinician_name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <span className="text-green-700 font-semibold text-lg">
                        {confirmationData.clinician_name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="text-gray-900 font-semibold">
                      {confirmationData.clinician_name}
                    </p>
                    <p className="text-gray-500 text-sm">
                      {confirmationData.clinician_title}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Contact */}
            <div className="pb-1">
              <p className="text-gray-500 text-sm mb-1">Questions?</p>
              <p className="text-gray-900">
                Call <a href={`tel:${practice.phone}`} className="text-green-700 hover:text-green-800 font-medium">{practice.phone ? formatPhoneNumber(practice.phone) : 'the practice directly'}</a>
              </p>
            </div>
          </div>

          {/* Powered by */}
          <p className="text-xs text-gray-400 text-center mt-6">
            Powered by <a href="https://helppet.ai" className="hover:text-gray-600">{APP_NAME}</a>
          </p>
        </div>
      </div>
    );
  }

  // Calendar step - Calendly-style layout
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4 md:p-8">
      <div className={`bg-white rounded-2xl shadow-xl border border-gray-200 relative transition-all duration-300 ${selectedDate ? 'w-full max-w-5xl' : 'w-full max-w-3xl'}`}>
        {/* Powered by badge - corner ribbon */}
        <div className="absolute top-0 right-0 overflow-hidden w-32 h-32 pointer-events-none z-10">
          <a 
            href="https://helppet.ai" 
            target="_blank" 
            rel="noopener noreferrer"
            className="absolute block text-center text-white no-underline pointer-events-auto transition-all duration-100 ease-in hover:brightness-110"
            style={{
              top: '24px',
              right: '-36px',
              width: '150px',
              padding: '8px 0 5px 0',
              backgroundColor: '#5a6670',
              boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.2)',
              fontWeight: 700,
              lineHeight: 1.2,
              transform: 'rotate(45deg)',
            }}
          >
            <span className="block text-[9px] tracking-wide opacity-90">POWERED BY</span>
            <span className="block text-[11px] font-bold">{APP_NAME}</span>
          </a>
        </div>

        <div className="flex flex-col md:flex-row min-h-[640px]">
          {/* Left Sidebar */}
          <div className="md:w-72 p-6 border-b md:border-b-0 md:border-r border-gray-200">
            {/* Practice Photo/Avatar - Centered */}
            <div className="flex justify-center mb-6">
              {practice.photo_url ? (
                <img 
                  src={practice.photo_url} 
                  alt={practice.name}
                  className="w-24 h-24 rounded-full object-cover border-2 border-gray-100"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                  <span className="text-3xl font-bold text-blue-600">
                    {practice.name.charAt(0)}
                  </span>
                </div>
              )}
            </div>

            {/* Horizontal Rule */}
            <hr className="border-gray-200 mb-6" />

            {/* Practice Info */}
            <div className="mb-6">
              <p className="text-sm text-gray-500 mb-1">{practice.name}</p>
              <h1 className="text-xl font-bold text-gray-900">Book Appointment</h1>
            </div>

            {/* Duration */}
            <div className="flex items-center gap-3 text-gray-600 mb-4">
              <Clock className="w-5 h-5 text-gray-400" />
              <span className="text-sm font-medium">30 min</span>
            </div>

            {/* Location */}
            {practice.address && (
              <div className="flex items-start gap-3 text-gray-600 mb-4">
                <MapPin className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <span className="text-sm">{practice.address}</span>
              </div>
            )}

            {/* Phone */}
            {practice.phone && (
              <div className="flex items-center gap-3 text-gray-600 mb-6">
                <Phone className="w-5 h-5 text-gray-400" />
                <a href={`tel:${practice.phone}`} className="text-sm hover:text-blue-600 transition-colors">
                  {formatPhoneNumber(practice.phone)}
                </a>
              </div>
            )}

            {/* Vet Selector */}
            {clinicians.length > 1 && (
              <div className="pt-4 border-t border-gray-100">
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Choose your veterinarian
                </label>
                <select
                  value={selectedClinicianId || ''}
                  onChange={(e) => handleClinicianChange(e.target.value || null)}
                  className="w-full text-sm border border-gray-300 rounded-lg py-2.5 px-3 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                >
                  <option value="">Any available</option>
                  {clinicians.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Main Content Area */}
          <div className="flex-1 flex flex-col md:flex-row">
            {/* Calendar */}
            <div className={`p-8 ${selectedDate ? 'md:border-r border-gray-200' : ''} flex-1`}>
              <h2 className="text-xl font-bold text-gray-900 mb-6">Select a Date & Time</h2>
              <CalendlyCalendar
                availableDates={availableDates}
                selectedDate={selectedDate}
                onDateSelect={handleDateSelect}
                month={currentMonth}
                onMonthChange={handleMonthChange}
                minDate={minDate}
                maxDate={maxDate}
                loading={slotsLoading}
              />
            </div>

            {/* Time Slots Panel - Only show when date is selected */}
            {selectedDate && (
              <div className="w-full md:w-60 p-6 pt-20 border-t md:border-t-0 border-gray-200 bg-white max-h-[640px] overflow-y-auto">
                <TimeSlotPanel
                  selectedDate={selectedDate}
                  slots={slotsForSelectedDate}
                  onSelectSlot={handleSlotSelect}
                  showVetName={false}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicBookingPage;
